import { useState } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Box,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as SoftwareIcon,
  Block as NonSoftwareIcon,
} from '@mui/icons-material';

interface Pattern {
  dbId?: number | null;
  patternText: string;
  patternType: string;
  classificationTarget: string;
  weight?: number | null;
  isActive: boolean;
  description?: string | null;
  source?: string | null;
  createdBy?: string | null;
  datetimeCreated?: string | null;
  isProbationary?: boolean | null;
  probationMetadata?: any | null;
}

interface PatternsDataGridProps {
  patterns: Pattern[];
  onEdit: (pattern: Pattern) => void;
  onDelete: (patternId: number) => Promise<void>;
  onToggleActive: (patternId: number, isActive: boolean) => Promise<void>;
  onActivateProbationary?: (patternId: number) => Promise<void>;
  onDeleteProbationary?: (patternId: number) => Promise<void>;
  loading?: boolean;
}

const PATTERN_TYPE_LABELS: Record<string, string> = {
  keyword_exact: 'Exact',
  keyword_contains: 'Contains',
  regex: 'Regex',
};

const TARGET_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  software: 'success',
  services: 'warning',
  reseller: 'info',
  agency: 'default',
  non_software: 'error',
};

const PatternsDataGrid = ({
  patterns,
  onEdit,
  onDelete,
  onToggleActive,
  onActivateProbationary,
  onDeleteProbationary,
  loading = false,
}: PatternsDataGridProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patternToDelete, setPatternToDelete] = useState<Pattern | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteClick = (pattern: Pattern) => {
    setPatternToDelete(pattern);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!patternToDelete || !patternToDelete.dbId) return;
    setDeleteLoading(true);
    try {
      await onDelete(patternToDelete.dbId);
      setDeleteDialogOpen(false);
      setPatternToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPatternToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'patternText',
      headerName: 'Pattern',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Pattern>) => {
        const metadata = params.row.probationMetadata ? JSON.parse(params.row.probationMetadata) : null;
        const isSoftwarePattern = params.row.classificationTarget === 'software';
        const tooltipText =
          params.row.description ||
          (metadata
            ? `AI-generated from keyword: "${metadata.source_keyword}"\nValidations: ${metadata.validation_count || 0}\nMatches: ${metadata.match_count || 0}`
            : 'No description');

        return (
          <Tooltip title={tooltipText}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              {/* Visual indicator for pattern type */}
              {isSoftwarePattern ? (
                <SoftwareIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
              ) : (
                <NonSoftwareIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
              )}
              <Box
                sx={{
                  fontFamily: params.row.patternType === 'regex' ? 'monospace' : 'inherit',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value}
              </Box>
              {params.row.isProbationary && (
                <Chip label="TEST" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 20 }} />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'patternType',
      headerName: 'Type',
      width: 100,
      renderCell: (params: GridRenderCellParams<Pattern>) => (
        <Chip label={PATTERN_TYPE_LABELS[params.value as string] || params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'classificationTarget',
      headerName: 'Target',
      width: 120,
      renderCell: (params: GridRenderCellParams<Pattern>) => (
        <Chip label={params.value} size="small" color={TARGET_COLORS[params.value as string] || 'default'} />
      ),
    },
    {
      field: 'weight',
      headerName: 'Weight',
      width: 80,
      type: 'number',
      renderCell: (params: GridRenderCellParams<Pattern>) => (
        <Box sx={{ fontWeight: 600 }}>{params.value?.toFixed(1) ?? '1.0'}</Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 80,
      renderCell: (params: GridRenderCellParams<Pattern>) => {
        const handleSwitchChange = (checked: boolean) => {
          if (!params.row.dbId) return;

          // If activating a test pattern, use special handler that also converts to regular
          if (params.row.isProbationary === true && checked && onActivateProbationary) {
            onActivateProbationary(params.row.dbId);
          } else {
            // Otherwise use normal toggle handler
            onToggleActive(params.row.dbId, checked);
          }
        };

        return <Switch checked={params.value} onChange={e => handleSwitchChange(e.target.checked)} size="small" />;
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
      renderCell: (params: GridRenderCellParams<Pattern>) => (
        <Chip label={params.value || 'manual'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Pattern>) => {
        // All patterns (both test and regular) show Edit and Delete actions
        return (
          <Box>
            <IconButton size="small" onClick={() => onEdit(params.row)} color="primary">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeleteClick(params.row)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <DataGrid
        rows={patterns}
        columns={columns}
        getRowId={row => row.dbId || 0}
        loading={loading}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: 'classificationTarget', sort: 'asc' }] },
        }}
        sx={{
          height: '100%',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        }}
        disableRowSelectionOnClick
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Pattern</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the pattern "{patternToDelete?.patternText}"? This will deactivate the
            pattern (soft delete).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatternsDataGrid;
